import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';
import ContactForm from '../../components/ContactForm/ContactForm';
import {
  trackPhoneCall,
  trackEmailClick,
} from '../../components/Analytics/Analytics';
import {
  KIT_DIGITAL_OFFICIAL_URL,
  KIT_DIGITAL_CATALOG_URL,
  KIT_DIGITAL_SOLUTIONS_URL,
  KIT_DIGITAL_PORTAL_URL,
  ACCELERA_PYME_PHONE,
  CONVOCATORIA_NOTICE,
  SEGMENTS,
  SOLUTION_CATEGORIES,
  PROCESS_STEPS,
  BENEFICIARY_REQUIREMENTS,
  FAQ,
  SERVICE_JSON_LD,
} from '../../data/kitDigitalContent';
import './AgentesDigitalizadoresPage.css';

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  url: 'https://e-asy.es/solicitud-kit-digital',
  mainEntity: FAQ.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const ModuloAmounts = ({ amounts, amountNote }) => (
  <div className="modulo-pricing">
    <div className="modulo-pricing-main">
      <span className="modulo-value">Hasta {amounts.III}</span>
      <span className="modulo-duration">Seg. III</span>
    </div>
    <div className="modulo-pricing-segments">
      <span>Seg. II: {amounts.II}</span>
      <span>Seg. I: {amounts.I}</span>
    </div>
    {amountNote && <p className="modulo-amount-note">{amountNote}</p>}
  </div>
);

const KitExternalLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="kit-external-link">
    {children}
  </a>
);

const AgentesDigitalizadoresPage = ({ darkMode, toggleDarkMode }) => {
  const handlePhoneClick = () => {
    trackPhoneCall('+34 822 68 50 12');
  };

  const handleEmailClick = () => {
    trackEmailClick('comercial@e-asy.es');
  };

  return (
    <>
      <SEO
        title="Kit Digital para pymes y autónomos | Agente Digitalizador E-ASY"
        description="E-ASY es Agente Digitalizador Adherido al Kit Digital. Te ayudamos a implantar ERP, automatización, web, integraciones y ciberseguridad con tu bono digital. Consultoría en Tenerife y España."
        keywords="kit digital, agente digitalizador, bono digital, digitalización pymes, ayudas digitalización, E-ASY, ERP kit digital, automatización procesos"
        canonical="https://e-asy.es/solicitud-kit-digital"
      />

      <JsonLd id="jsonld-kit-digital-faq" data={FAQ_SCHEMA} />
      <JsonLd id="jsonld-kit-digital-service" data={SERVICE_JSON_LD} />

      <AnalyticsTracker
        pageTitle="Kit Digital - E-ASY"
        pageLocation="/solicitud-kit-digital"
        darkMode={darkMode}
      />

      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className={`agentes-digitalizadores-page ${darkMode ? 'dark' : 'light'}`}>
        <section className={`agentes-hero ${darkMode ? 'dark' : 'light'}`}>
          <div className="agentes-hero-content">
            <div className="agentes-hero-text">
              <h1 className="agentes-hero-title">Kit Digital con E-ASY</h1>
              <p className="agentes-hero-subtitle">
                Programa de ayudas del Gobierno de España para digitalizar pymes, microempresas
                y autónomos. E-ASY es <strong>Agente Digitalizador Adherido</strong>: te
                acompañamos a elegir e implantar soluciones del catálogo oficial dentro de tu
                bono digital.
              </p>
              <div className="agentes-hero-stats">
                <div className="hero-stat">
                  <div className="hero-stat-number">ADA</div>
                  <div className="hero-stat-label">adherido al catálogo Red.es</div>
                </div>
                <div className="hero-stat">
                  <div className="hero-stat-number">12</div>
                  <div className="hero-stat-label">categorías oficiales de solución</div>
                </div>
                <div className="hero-stat">
                  <div className="hero-stat-number">ERP+</div>
                  <div className="hero-stat-label">gestión, web, datos y seguridad</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={`kit-digital-notice ${darkMode ? 'dark' : 'light'}`}>
          <div className="kit-digital-notice-content">
            <p className="kit-digital-notice-text">{CONVOCATORIA_NOTICE}</p>
            <p className="kit-digital-notice-links">
              <KitExternalLink href={KIT_DIGITAL_OFFICIAL_URL}>
                Información oficial del programa
              </KitExternalLink>
              {' · '}
              <KitExternalLink href={KIT_DIGITAL_CATALOG_URL}>
                Catálogo de digitalizadores
              </KitExternalLink>
              {' · '}
              Teléfono Acelera Pyme:{' '}
              <a
                href={`tel:${ACCELERA_PYME_PHONE.replace(/\s/g, '')}`}
                className="kit-external-link"
              >
                {ACCELERA_PYME_PHONE}
              </a>
            </p>
          </div>
        </section>

        <section className={`beneficios-section ${darkMode ? 'dark' : 'light'}`}>
          <div className="beneficios-content">
            <div className="beneficios-header">
              <h2 className="beneficios-title">¿Qué es el Kit Digital?</h2>
              <p className="beneficios-subtitle">
                Iniciativa financiada con fondos europeos (Next Generation EU) para que las
                empresas contraten soluciones digitales del mercado a través de agentes
                acreditados, no para repartir dinero sin un proyecto asociado.
              </p>
            </div>

            <div className="beneficios-grid">
              <div className="beneficio-card">
                <div className="beneficio-icon">🎫</div>
                <h3 className="beneficio-title">Bono digital</h3>
                <p className="beneficio-description">
                  Si cumples requisitos y te conceden ayuda, dispones de un importe máximo según
                  el tamaño de tu empresa (segmento) para contratar soluciones del catálogo.
                </p>
              </div>
              <div className="beneficio-card">
                <div className="beneficio-icon">📋</div>
                <h3 className="beneficio-title">Catálogo oficial</h3>
                <p className="beneficio-description">
                  Solo valen soluciones y ADA incluidos en el programa. Puedes verificar a E-ASY
                  en el{' '}
                  <KitExternalLink href={KIT_DIGITAL_CATALOG_URL}>
                    catálogo de digitalizadores
                  </KitExternalLink>
                  .
                </p>
              </div>
              <div className="beneficio-card">
                <div className="beneficio-icon">🛠️</div>
                <h3 className="beneficio-title">Implantación real</h3>
                <p className="beneficio-description">
                  El objetivo es mejorar tu gestión: web, CRM, procesos, factura electrónica,
                  BI o seguridad, con plazos y entregables justificables ante Red.es.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={`modulos-section ${darkMode ? 'dark' : 'light'}`}>
          <div className="modulos-content">
            <div className="modulos-header">
              <h2 className="modulos-title">Importe del bono por segmento</h2>
              <p className="modulos-subtitle">
                El techo de ayuda depende del número de empleados. Cada categoría de solución
                tiene además un importe máximo propio dentro de ese bono.
              </p>
            </div>

            <div className="kit-segments-table-wrap">
              <table className="kit-segments-table">
                <thead>
                  <tr>
                    <th>Segmento</th>
                    <th>Empresa</th>
                    <th>Bono máximo</th>
                  </tr>
                </thead>
                <tbody>
                  {SEGMENTS.map((seg) => (
                    <tr key={seg.id}>
                      <td>{seg.label}</td>
                      <td>
                        {seg.employees}
                        {seg.note && (
                          <span className="kit-segment-note"> {seg.note}</span>
                        )}
                      </td>
                      <td>
                        <strong>{seg.bono}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modulos-info">
              <div className="modulos-highlight kit-modulos-highlight">
                <h4 className="kit-highlight-title">Importante</h4>
                <p className="kit-highlight-text">
                  El bono no se cobra como transferencia libre: se aplica al contratar
                  soluciones con un ADA. Los topes por categoría y el reparto entre varias
                  soluciones están publicados en{' '}
                  <KitExternalLink href={KIT_DIGITAL_SOLUTIONS_URL}>
                    soluciones digitales (Acelera Pyme)
                  </KitExternalLink>
                  . Te ayudamos a encajar prioridades (por ejemplo ERP + web) sin
                  sobrepasar tu segmento.
                </p>
              </div>
              <div className="modulos-requirements">
                <h4 className="requirements-title">Requisitos habituales del beneficiario</h4>
                <ul className="requirements-list">
                  {BENEFICIARY_REQUIREMENTS.map((req) => (
                    <li key={req}>{req}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className={`proceso-section ${darkMode ? 'dark' : 'light'}`}>
          <div className="proceso-content">
            <div className="proceso-header">
              <h2 className="proceso-title">Cómo funciona el proceso</h2>
              <p className="proceso-subtitle">
                Resumen del flujo oficial para pymes y autónomos. Nosotros entramos como ADA en
                los pasos de propuesta e implantación.
              </p>
            </div>

            <div className="proceso-steps">
              {PROCESS_STEPS.map((item) => (
                <div className="proceso-step" key={item.step}>
                  <div className="step-number">{item.step}</div>
                  <div className="step-content">
                    <h3 className="step-title">{item.title}</h3>
                    <p className="step-description">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="kit-portal-link">
              Si ya tienes bono: gestiona acuerdos en el{' '}
              <KitExternalLink href={KIT_DIGITAL_PORTAL_URL}>
                portal de gestión del Kit Digital
              </KitExternalLink>
              .
            </p>
          </div>
        </section>

        <section className={`modulos-section ${darkMode ? 'dark' : 'light'}`}>
          <div className="modulos-content">
            <div className="modulos-header">
              <h2 className="modulos-title">Categorías de solución del programa</h2>
              <p className="modulos-subtitle">
                Importes máximos de subvención por categoría según el catálogo oficial (no
                precio de mercado). El ADA debe respetar estos topes en la propuesta.
              </p>
            </div>

            <div className="modulos-grid">
              {SOLUTION_CATEGORIES.map((cat) => (
                <div
                  className={`modulo-card ${cat.easy ? 'modulo-card--easy' : ''}`}
                  key={cat.id}
                >
                  {cat.easy && <span className="modulo-badge">Implantación E-ASY</span>}
                  <h3 className="modulo-title">{cat.title}</h3>
                  <p className="modulo-description">{cat.description}</p>
                  <div className="modulo-details">
                    <ModuloAmounts amounts={cat.amounts} amountNote={cat.amountNote} />
                  </div>
                  {cat.link && (
                    <Link to={cat.link} className="kit-cta-link">
                      Ver servicio
                    </Link>
                  )}
                  {!cat.easy && (
                    <p className="modulo-availability">Consultar disponibilidad con E-ASY</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={`kit-faq-section ${darkMode ? 'dark' : 'light'}`}>
          <div className="kit-faq-content">
            <h2 className="kit-faq-title">Preguntas frecuentes</h2>
            {FAQ.map((item) => (
              <details className="kit-faq-item" key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="agentes-main">
          <div className="agentes-content">
            <div className="agentes-form-section">
              <div className="form-header">
                <h2 className="form-title">Cuéntanos tu caso</h2>
                <p className="form-subtitle">
                  Indica si ya tienes bono, tu sector y qué quieres digitalizar (ERP, web,
                  procesos, etc.). Te respondemos para ver encaje con el catálogo y próximos
                  pasos.
                </p>
              </div>

              <ContactForm
                darkMode={darkMode}
                source="kit-digital"
                trackingEvent="kit_digital_contact_form"
                pageName="Kit Digital"
                subjectPrefix="[Kit Digital]"
                emptyMessageFallback="Solicitud de Kit Digital desde /solicitud-kit-digital"
              />
            </div>

            <div className="agentes-info-section">
              <div className="info-header">
                <h3 className="info-title">Enlaces y contacto</h3>
                <p className="info-subtitle">
                  Verifica siempre la información actualizada en la web oficial del programa.
                </p>
              </div>

              <div className="info-cards">
                <div className="info-card">
                  <h4 className="card-title">Web oficial</h4>
                  <ul className="card-list">
                    <li>
                      <KitExternalLink href={KIT_DIGITAL_OFFICIAL_URL}>
                        Programa Kit Digital (Acelera Pyme)
                      </KitExternalLink>
                    </li>
                    <li>
                      <KitExternalLink href={KIT_DIGITAL_SOLUTIONS_URL}>
                        Soluciones y topes por categoría
                      </KitExternalLink>
                    </li>
                    <li>
                      <KitExternalLink href={KIT_DIGITAL_CATALOG_URL}>
                        Buscar E-ASY en el catálogo
                      </KitExternalLink>
                    </li>
                  </ul>
                </div>

                <div className="info-card">
                  <h4 className="card-title">E-ASY</h4>
                  <ul className="card-list">
                    <li>Agente Digitalizador Adherido</li>
                    <li>Base en Santa Cruz de Tenerife</li>
                    <li>Proyectos en Canarias y resto de España</li>
                    <li>
                      <Link to="/servicios">Todos los servicios</Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-content">
                    <h4 className="method-title">Teléfono</h4>
                    <p className="method-value">
                      <a href="tel:+34822685012" onClick={handlePhoneClick}>
                        +34 822 68 50 12
                      </a>
                    </p>
                    <p className="method-description">Lun–Vie 9:00 – 17:00</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-content">
                    <h4 className="method-title">Email</h4>
                    <p className="method-value">
                      <a href="mailto:comercial@e-asy.es" onClick={handleEmailClick}>
                        comercial@e-asy.es
                      </a>
                    </p>
                    <p className="method-description">Respuesta en 24–48 h laborables</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default AgentesDigitalizadoresPage;
